import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Plug, RefreshCw, Plus, AlertTriangle, CheckCircle2, Clock } from "lucide-react";
import { formatNum } from "../lib/utils";
import { toast } from "sonner";

const HEALTH = {
  ok: { tone: "success", label: "Healthy", icon: CheckCircle2 },
  auth_expired: { tone: "danger", label: "Auth expired", icon: AlertTriangle },
  not_configured: { tone: "warning", label: "Not configured", icon: Clock },
};

export default function Connectors() {
  const [catalogue, setCatalogue] = useState([]);
  const [connected, setConnected] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    const [cat, conn] = await Promise.all([
      api.get("/connectors/catalogue"),
      api.get("/connectors/connected"),
    ]);
    setCatalogue(cat.data.connectors);
    setConnected(conn.data.connectors);
  };
  useEffect(() => { load(); }, []);

  const action = async (code, act) => {
    setBusy(code + act);
    try {
      const { data } = await api.post("/connectors/action", { connector_code: code, action: act });
      toast.success(data.note || `${act} success`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(null);
  };

  const isConnected = (code) => connected.find((c) => c.connector_code === code);

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div>
        <div className="label-eyebrow mb-1">Integrations · ERP / HRMS / EHS</div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Data Connectors</h1>
        <p className="text-muted-foreground text-sm mt-1 max-w-2xl">
          Automated extraction from enterprise systems and Singapore regulators.{" "}
          <Badge variant="warning" className="ml-1">SIMULATED — Demo Mode</Badge>
        </p>
      </div>

      {/* Active connections */}
      <div>
        <div className="label-eyebrow mb-3">Active connections — {connected.length}</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {connected.map((c) => {
            const h = HEALTH[c.health] || HEALTH.ok;
            const HI = h.icon;
            return (
              <div key={c.id} className="card-flat p-5" data-testid={`conn-${c.connector_code}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{c.meta?.type}</div>
                    <h4 className="font-display font-semibold">{c.meta?.name}</h4>
                  </div>
                  <Badge variant={h.tone}><HI className="h-3 w-3 mr-1" /> {h.label}</Badge>
                </div>
                {c.last_sync_at ? (
                  <div className="text-xs font-mono mb-3">
                    <div className="text-muted-foreground">Last sync</div>
                    <div>{new Date(c.last_sync_at).toLocaleString("en-SG", {dateStyle: "medium", timeStyle: "short"})}</div>
                    <div className="mt-1 text-muted-foreground">{formatNum(c.records_last_sync)} records</div>
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground mb-3">Never synced</div>
                )}
                {c.error_message && <div className="text-xs text-red-400 bg-red-500/10 p-2 rounded-sm mb-3">{c.error_message}</div>}
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1" disabled={busy === c.connector_code + "sync"} onClick={() => action(c.connector_code, "sync")} data-testid="sync-btn">
                    <RefreshCw className={`h-3 w-3 ${busy === c.connector_code + "sync" ? "animate-spin" : ""}`} /> Sync
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => action(c.connector_code, "disconnect")} data-testid="disconnect-btn">Remove</Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Catalogue — available */}
      <div>
        <div className="label-eyebrow mb-3">Available connectors</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {catalogue.map((c) => {
            const connected_ = isConnected(c.code);
            return (
              <div key={c.code} className="card-flat p-5" data-testid={`avail-${c.code}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="h-10 w-10 bg-surface border border-border rounded-sm flex items-center justify-center">
                    <Plug className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <Badge variant="outline" className="text-[10px]">{c.type}</Badge>
                </div>
                <h4 className="font-display font-semibold mb-1">{c.name}</h4>
                <div className="text-xs text-muted-foreground mb-3">{c.category}</div>
                <div className="text-xs font-mono mb-3">
                  <div className="text-muted-foreground mb-1">Extracts</div>
                  <ul className="space-y-0.5">
                    {c.extracts.map((e) => <li key={e}>· {e}</li>)}
                  </ul>
                </div>
                <div className="text-[10px] font-mono text-muted-foreground mb-3">Auth: {c.auth}</div>
                {connected_ ? (
                  <Badge variant="success" className="w-full justify-center">Connected</Badge>
                ) : (
                  <Button variant="primary" size="sm" className="w-full" disabled={busy === c.code + "connect"} onClick={() => action(c.code, "connect")} data-testid="connect-btn">
                    <Plus className="h-3 w-3" /> Connect
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
